import {
  Card, Center, Container, Group, Loader, SimpleGrid, Stack, Text, Title,
} from '@mantine/core';
import { Suspense, lazy } from 'react';
import { Notifications } from '@mantine/notifications';
import {
  IconBriefcase, IconBucket, IconBuildingBank, IconChartLine, IconChecklist, IconClipboardList, IconFileText, IconHistory,
  IconInbox, IconListSearch, IconPresentation, IconReceiptTax, IconReport, IconShieldCheck,
  IconTrendingUp, IconUsers,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import { accountInsuranceApi } from './api/accountInsurance';
import { accountsApi } from './api/accounts';
import { agreementsApi } from './api/agreements';
import { changeRequestsApi } from './api/changeRequests';
import { useMyTaskCounts } from './hooks/useAgreementTasks';
import { personsApi } from './api/persons';
import { useCurrentUser } from './hooks/useAuth';
import { usePermissions } from './hooks/usePermissions';
import type { ChangeRequest } from './api/types';
import { AppShell } from './components/AppShell';
import { NextThirtyDaysCard } from './components/NextThirtyDaysCard';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { AccountDetailPage } from './pages/AccountDetailPage';
import { AccountInsuranceDetailPage } from './pages/AccountInsuranceDetailPage';
import { AccountInsuranceListPage } from './pages/AccountInsuranceListPage';
import { AccountPartiesListPage } from './pages/AccountPartiesListPage';
import { AccountPartyDetailPage } from './pages/AccountPartyDetailPage';
import { AccountsListPage } from './pages/AccountsListPage';
import { AgreementDetailPage } from './pages/AgreementDetailPage';
import { AgreementPartiesListPage } from './pages/AgreementPartiesListPage';
import { AgreementPartyDetailPage } from './pages/AgreementPartyDetailPage';
import { AgreementsListPage } from './pages/AgreementsListPage';
import { AuditPage } from './pages/AuditPage';
import { CompliancePage } from './pages/CompliancePage';
import { ExtractionPage } from './pages/ExtractionPage';
import { PermissionsAdminPage } from './pages/PermissionsAdminPage';
import { PortfolioDataPage } from './pages/PortfolioDataPage';
import { AdminPage } from './pages/AdminPage';
import { ClientReportsPage } from './pages/ClientReportsPage';
import { FirmFinancialsPage } from './pages/FirmFinancialsPage';
import { LegalTemplatesPage } from './pages/LegalTemplatesPage';
import { PresentationLibraryPage } from './pages/PresentationLibraryPage';
import { SaaPage } from './pages/SaaPage';
import { RegulatoryPage } from './pages/RegulatoryPage';
// Code-split: /reports pulls the Vega charting family (~840KB). Lazy-loading it
// (+ the vite `charts` manualChunk) keeps that weight off the initial app load;
// only visiting /reports fetches it. Named export → mapped to default for lazy().
const ReportsPage = lazy(() =>
  import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
import { TranslationsPage } from './pages/TranslationsPage';
import { SaaAdminPage } from './pages/SaaAdminPage';
import { CapitalGainsPage } from './pages/CapitalGainsPage';
import { PortfoliosPage } from './pages/PortfoliosPage';
import { ModelsPage } from './pages/ModelsPage';
import { BanksPage } from './pages/BanksPage';
import { DataExportsPage } from './pages/DataExportsPage';
import { RmCommissionsPage } from './pages/RmCommissionsPage';
import { ClassificationPage } from './pages/ClassificationPage';
import { SecuritiesPage } from './pages/SecuritiesPage';
import { StandardFeeSchedulePage } from './pages/StandardFeeSchedulePage';
import { FeeRunBatchPage } from './pages/FeeRunBatchPage';
import { FeesPage } from './pages/FeesPage';
import { RmsPage } from './pages/RmsPage';
import { LoginPage } from './pages/LoginPage';
import { PersonDetailPage } from './pages/PersonDetailPage';
import { PersonsListPage } from './pages/PersonsListPage';
import { PersonNameReviewPage } from './pages/PersonNameReviewPage';
import { UserDetailPage } from './pages/UserDetailPage';
import { UsersListPage } from './pages/UsersListPage';
import { ChangeRequestsListPage } from './pages/ChangeRequestsListPage';
import { ChangeRequestDetailPage } from './pages/ChangeRequestDetailPage';
import { MyChangeRequestsPage } from './pages/MyChangeRequestsPage';
import { MyTasksPage } from './pages/MyTasksPage';
import { MyFollowUpsPage } from './pages/MyFollowUpsPage';
import { HelpPage } from './pages/HelpPage';
import { InsurancePolicyPartiesListPage } from './pages/InsurancePolicyPartiesListPage';
import { InsurancePolicyPartyDetailPage } from './pages/InsurancePolicyPartyDetailPage';
import { AlertModalProvider } from './components/AlertModalProvider';
import { DirtyFormProvider } from './components/DirtyFormProvider';

// Task #251 (2026-06-04): Root layout for the DataRouter migration.
// Holds the providers that need to live INSIDE the router context so the
// dirty-form-guard can use `useBlocker` (Task #100 follow-up in Stage 2).
// Previously these providers wrapped <App /> from outside <BrowserRouter>
// in main.tsx; with createBrowserRouter they have to be inside the route
// tree to participate in router context.
function RootLayout() {
  return (
    <DirtyFormProvider>
      <AlertModalProvider>
        <Notifications position="top-center" limit={5} />
        <Outlet />
      </AlertModalProvider>
    </DirtyFormProvider>
  );
}

// Task #251 (2026-06-04): route tree as a RouteObject array, consumed by
// createBrowserRouter() in main.tsx. The structure mirrors the previous
// JSX <Routes>/<Route> declaration 1:1 — same paths, same elements,
// same layout-route hierarchy (ProtectedRoute → AppShell → child pages).
// Adding a RootLayout wrapper at the very top so the providers above
// participate in router context (necessary for Stage 2's useBlocker).
export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      {
        element: <ProtectedRoute><AppShell /></ProtectedRoute>,
        children: [
          { index: true, element: <Dashboard /> },
          { path: 'persons', element: <PersonsListPage /> },
          { path: 'persons/new', element: <PersonDetailPage /> },
          // P16b G1: the one-off name-split review (static path, ranked above persons/:id).
          { path: 'persons/name-review', element: <PersonNameReviewPage /> },
          { path: 'persons/:id', element: <PersonDetailPage /> },
          { path: 'agreements', element: <AgreementsListPage /> },
          { path: 'agreements/new', element: <AgreementDetailPage /> },
          { path: 'agreements/:id', element: <AgreementDetailPage /> },
          { path: 'agreement-parties', element: <AgreementPartiesListPage /> },
          { path: 'agreement-parties/new', element: <AgreementPartyDetailPage /> },
          { path: 'agreement-parties/:id', element: <AgreementPartyDetailPage /> },
          { path: 'accounts', element: <AccountsListPage /> },
          { path: 'accounts/new', element: <AccountDetailPage /> },
          { path: 'accounts/:id', element: <AccountDetailPage /> },
          { path: 'account-parties', element: <AccountPartiesListPage /> },
          { path: 'account-parties/new', element: <AccountPartyDetailPage /> },
          { path: 'account-parties/:id', element: <AccountPartyDetailPage /> },
          { path: 'account-insurance', element: <AccountInsuranceListPage /> },
          { path: 'account-insurance/new', element: <AccountInsuranceDetailPage /> },
          { path: 'account-insurance/:id', element: <AccountInsuranceDetailPage /> },
          { path: 'audit', element: <AuditPage /> },
          { path: 'admin', element: <AdminPage /> },
          { path: 'admin/extraction', element: <ExtractionPage /> },
          { path: 'admin/permissions', element: <PermissionsAdminPage /> },
          { path: 'admin/saa', element: <SaaAdminPage /> },
          { path: 'admin/capital-gains', element: <CapitalGainsPage /> },
          { path: 'admin/portfolios', element: <PortfoliosPage /> },
          { path: 'admin/models', element: <ModelsPage /> },
          // TRADES-RM (Pedro 2026-10-23): trading is a button on View portfolio; the old address leads there.
          { path: 'admin/trades', element: <Navigate to="/admin/portfolios?tab=view" replace /> },
          { path: 'admin/banks', element: <BanksPage /> },
          { path: 'admin/data-exports', element: <DataExportsPage /> },
          { path: 'admin/commissions', element: <RmCommissionsPage /> },
          { path: 'admin/classification', element: <ClassificationPage /> },
          { path: 'admin/securities', element: <SecuritiesPage /> },
          { path: 'admin/fee-schedule', element: <StandardFeeSchedulePage /> },
          { path: 'admin/fee-run-batch', element: <FeeRunBatchPage /> },
          { path: 'admin/fees', element: <FeesPage /> },
          { path: 'admin/rms', element: <RmsPage /> },
          // Returns folded into the Portfolio Data page (nav reorg 2026-07-04, Inc 4);
          // the standalone route now redirects to its tab (keep old bookmarks working).
          { path: 'admin/returns', element: <Navigate to="/admin/portfolio-data?tab=returns" replace /> },
          { path: 'admin/translations', element: <TranslationsPage /> },
          { path: 'admin/portfolio-data', element: <PortfolioDataPage /> },
          { path: 'admin/client-reports', element: <ClientReportsPage /> },
          { path: 'admin/firm-financials', element: <FirmFinancialsPage /> },
          { path: 'admin/regulatory', element: <RegulatoryPage /> },
          { path: 'reports', element: (
            <Suspense fallback={<Center h="60vh"><Loader /></Center>}>
              <ReportsPage />
            </Suspense>
          ) },
          // Former EOM sub-page is now a tab; redirect the old path.
          { path: 'admin/portfolio-data/eom', element: <Navigate to="/admin/portfolio-data?tab=eom" replace /> },
          // Old standalone link → Admin hub (keep old bookmarks working).
          { path: 'portfolio-data', element: <Navigate to="/admin/portfolio-data" replace /> },
          { path: 'insurance-policy-parties', element: <InsurancePolicyPartiesListPage /> },
          { path: 'insurance-policy-parties/new', element: <InsurancePolicyPartyDetailPage /> },
          { path: 'insurance-policy-parties/:id', element: <InsurancePolicyPartyDetailPage /> },
          { path: 'change-requests', element: <ChangeRequestsListPage /> },
          { path: 'change-requests/:id', element: <ChangeRequestDetailPage /> },
          { path: 'my-change-requests', element: <MyChangeRequestsPage /> },
          { path: 'my-tasks', element: <MyTasksPage /> },
          { path: 'my-follow-ups', element: <MyFollowUpsPage /> },
          { path: 'compliance', element: <CompliancePage /> },
          { path: 'users', element: <UsersListPage /> },
          { path: 'users/new', element: <UserDetailPage /> },
          { path: 'users/:id', element: <UserDetailPage /> },
          { path: 'legal-templates', element: <LegalTemplatesPage /> },
          { path: 'library', element: <PresentationLibraryPage /> },
          { path: 'saa', element: <SaaPage /> },
          { path: 'help', element: <HelpPage /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
];

interface CountCardProps {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number | string }>;
  count: number | undefined;
  color: string;
}

function CountCard({ to, label, icon: Icon, count, color }: CountCardProps) {
  return (
    <Card component={Link} to={to} withBorder shadow="sm"
      style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="sm" c="dimmed">{label}</Text>
          <Title order={2} c={color}>{count ?? '—'}</Title>
        </Stack>
        <Icon size={28} />
      </Group>
    </Card>
  );
}

/**
 * Feature 3 (2026-05-28): role-aware Pending Change Requests card. Replaces
 * the prior Audit log entries card on the landing page.
 *
 * Audience-dependent semantics share one card (Pedro's call — "Pending
 * Change Requests" reads cleanly for both):
 *   - Checker (admin, compliance): count of ALL system-wide pending CRs
 *     waiting on someone to approve. Caption: "awaiting your approval".
 *     Click navigates to /change-requests. Color thresholds reflect
 *     queue-backlog urgency.
 *   - Maker (rm, staff): count of THEIR OWN filings still awaiting
 *     Compliance. Caption: "awaiting Compliance". Click navigates to
 *     /my-change-requests. Color stays neutral — the user can't reduce
 *     this count themselves.
 *   - read_only: card hidden (they don't file, don't approve).
 *
 * Backend filtering is already correct: Beta #9+#10 made the existing
 * GET /change-requests?status=PENDING return all pending for Checkers,
 * only-own pending for Makers. Frontend reuses that without changes.
 *
 * The fetched list is capped at limit=200; the count comes from the
 * server-side total, the oldest-pending date is min(requested_at)
 * across the returned items. A pending queue >200 is a much bigger
 * problem than a slightly-skewed "oldest" label.
 *
 * Live freshness mirrors MyChangeRequestsPage — refetchOnMount=always
 * + 30s polling — the dashboard is the kind of page users keep open.
 */
function PendingChangeRequestsCard() {
  const { user } = useCurrentUser();
  const isChecker = user?.role === 'admin' || user?.role === 'compliance';
  const isMaker = user?.role === 'rm' || user?.role === 'staff';

  // read_only doesn't file or approve — hide.
  const visible = isChecker || isMaker;

  const q = useQuery({
    queryKey: ['count', 'change-requests-pending'],
    queryFn: () => changeRequestsApi.list({ status: 'PENDING' }, 200, 0),
    enabled: visible,
    refetchOnMount: 'always',
    refetchInterval: 30_000,
    staleTime: 0,
  });

  if (!visible) return null;

  const total = q.data?.total;
  const items: ChangeRequest[] = q.data?.items ?? [];

  // Oldest-pending age. Backend orders by requested_at desc, so the oldest
  // is the *last* item in the page. min() across all fetched items is
  // robust either way.
  let oldestDays: number | null = null;
  if (items.length > 0) {
    const oldestTs = items.reduce<number>((acc, cr) => {
      const t = new Date(cr.requested_at).getTime();
      return acc === 0 || t < acc ? t : acc;
    }, 0);
    if (oldestTs > 0) {
      const ms = Date.now() - oldestTs;
      oldestDays = Math.floor(ms / (1000 * 60 * 60 * 24));
    }
  }

  // Color thresholds — Checker only. Makers stay neutral regardless of count.
  let color = 'dark';
  if (isChecker && total !== undefined) {
    if (total === 0) color = 'gray.6';
    else if (total <= 5) color = 'azul';
    else if (total <= 10) color = 'laranja';
    else color = 'red.7';
  } else {
    color = 'azul';
  }

  const target = isChecker ? '/change-requests' : '/my-change-requests';
  const caption = isChecker ? 'awaiting your approval' : 'awaiting Compliance';

  return (
    <Card component={Link} to={target} withBorder shadow="sm"
      style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="sm" c="dimmed">Pending Change Requests</Text>
          <Title order={2} c={color}>{total ?? '—'}</Title>
          <Text size="xs" c="dimmed">{caption}</Text>
          {oldestDays !== null && (total ?? 0) > 0 && (
            <Text size="xs" c="dimmed">
              Oldest pending: {oldestDays} day{oldestDays === 1 ? '' : 's'}
            </Text>
          )}
        </Stack>
        <IconInbox size={28} />
      </Group>
    </Card>
  );
}


/**
 * Feature 4 (2026-05-28): role-aware My Tasks card.
 *
 *   - Maker: count of OPEN tasks where they are creator OR assignee.
 *     Caption: "open" + "X overdue" sub-line if any.
 *   - Admin: count of ALL alive OPEN tasks. Caption: "open" + overdue.
 *
 * Colour: green if 0 open and 0 overdue, amber if 1+ open and 0 overdue,
 * red if any overdue (overdue overrides count thresholds — "1 overdue"
 * deserves more urgency than "10 not yet due").
 */
function MyTasksCard() {
  const { user } = useCurrentUser();
  const isChecker = user?.role === 'admin' || user?.role === 'compliance';
  const isMaker = user?.role === 'rm' || user?.role === 'staff';
  const visible = isChecker || isMaker;

  const q = useMyTaskCounts();
  if (!visible) return null;

  const open = q.data?.open_count;
  const overdue = q.data?.overdue_count ?? 0;
  const isAdmin = user?.role === 'admin';

  let color = 'azul';
  if (open === 0 && overdue === 0) color = 'gray.6';
  else if (overdue > 0) color = 'red.7';
  else if ((open ?? 0) > 0) color = 'laranja';

  const target = isAdmin ? '/my-tasks' : '/my-tasks';
  const label = isAdmin ? 'All Tasks' : 'My Tasks';
  const caption = isAdmin ? 'open across the firm' : 'open (you create or assigned)';

  return (
    <Card component={Link} to={target} withBorder shadow="sm"
      style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="sm" c="dimmed">{label}</Text>
          <Title order={2} c={color}>{open ?? '—'}</Title>
          <Text size="xs" c="dimmed">{caption}</Text>
          {overdue > 0 && (
            <Text size="xs" c="red.7" fw={500}>
              {overdue} overdue
            </Text>
          )}
        </Stack>
        <IconChecklist size={28} />
      </Group>
    </Card>
  );
}

/** P45 (Pedro 2026-10-15, F5): RM and Compliance reach five Admin pages, read-only and
 *  firm-wide, from Welcome-page tiles (Admins have them on the Admin page). The pages hide
 *  every edit and data export for them; the server refuses those too. */
const ADMIN_PAGE_READ_TILES = [
  { to: '/admin/portfolio-data', small: 'AuM & Returns', title: 'Returns', hint: 'Monthly returns by agreement and by month', icon: IconTrendingUp },
  { to: '/admin/securities', small: 'Securities', title: 'Securities', hint: 'The securities list and their classifications', icon: IconListSearch },
  { to: '/admin/capital-gains', small: 'Capital gains', title: 'Capital gains', hint: 'Rebuild portfolios and compute the tax', icon: IconReceiptTax },
  { to: '/admin/client-reports', small: 'Client reports', title: 'Monthly reports', hint: 'View and download the produced reports', icon: IconReport },
  { to: '/admin/fees', small: 'Fees', title: 'Fees history', hint: 'Computed fees and invoices, by agreement or month', icon: IconHistory },
] as const;

function AdminPageReadTiles() {
  const { user } = useCurrentUser();
  if (!user || (user.role !== 'rm' && user.role !== 'compliance')) return null;
  return (
    <>
      {ADMIN_PAGE_READ_TILES.map((t) => (
        <Card key={t.to} component={Link} to={t.to} withBorder shadow="sm"
          style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Text size="sm" c="dimmed">{t.small}</Text>
              <Title order={2} c="petroleo">{t.title}</Title>
              <Text size="xs" c="dimmed">{t.hint} · read-only</Text>
            </Stack>
            <t.icon size={28} />
          </Group>
        </Card>
      ))}
    </>
  );
}

/** HP-R (Pedro 2026-10-20): "a Portfolios card on the Welcome page for RMs, without any
 *  Upload features" — for whoever holds `portfolios.read` (RM and Compliance by default);
 *  Admins reach Portfolios from the Admin page. Read-only and firm-wide (portfolios are not
 *  yet linked to agreements). */
function PortfoliosReadTile() {
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  if (!user || user.role === 'admin' || !can('portfolios.read')) return null;
  return (
    <Card component={Link} to="/admin/portfolios?from=welcome" withBorder shadow="sm"
      style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="sm" c="dimmed">Client portfolios</Text>
          <Title order={2} c="petroleo">Portfolios</Title>
          <Text size="xs" c="dimmed">View portfolio and View security · read-only</Text>
        </Stack>
        <IconBriefcase size={28} />
      </Group>
    </Card>
  );
}

/** Model Portfolios M1 (Pedro 2026-10-26: "separate Models card"): for whoever holds
 *  `portfolios.read` (RM and Compliance by default); Admins reach Models from the Admin page.
 *  Reading only, unless `model.manage` was granted. */
function ModelsReadTile() {
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  if (!user || user.role === 'admin' || !can('portfolios.read')) return null;
  return (
    <Card component={Link} to="/admin/models?from=welcome" withBorder shadow="sm"
      style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="sm" c="dimmed">Model portfolios</Text>
          <Title order={2} c="petroleo">Models</Title>
          <Text size="xs" c="dimmed">{can('model.manage') ? 'Buckets of Approved securities and cash' : 'Buckets of Approved securities and cash · read-only'}</Text>
        </Stack>
        <IconBucket size={28} />
      </Group>
    </Card>
  );
}

function Dashboard() {
  useDocumentTitle('Dashboard');

  const persons          = useQuery({ queryKey: ['count','persons'],          queryFn: () => personsApi.list({ limit: 1 }),          staleTime: 60_000 });
  const agreements       = useQuery({ queryKey: ['count','agreements'],       queryFn: () => agreementsApi.list({ limit: 1 }),       staleTime: 60_000 });
  const accounts         = useQuery({ queryKey: ['count','accounts'],         queryFn: () => accountsApi.list({ limit: 1 }),         staleTime: 60_000 });
  const accountInsurance = useQuery({ queryKey: ['count','accountInsurance'], queryFn: () => accountInsuranceApi.list({ limit: 1 }), staleTime: 60_000 });

  return (
    <Container size="xl">
      <Stack>
        <Title order={2}>Welcome</Title>
        <Text c="dimmed">Live counts of every entity in your CRM. Click a card to drill in.</Text>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          <CountCard to="/agreements"         label="Agreements"          icon={IconClipboardList} color="petroleo"  count={agreements.data?.total} />
          <CountCard to="/persons"            label="Persons"             icon={IconUsers}         color="azul"      count={persons.data?.total} />
          <CountCard to="/accounts"           label="Accounts"            icon={IconBuildingBank}  color="verde"     count={accounts.data?.total} />
          <CountCard to="/account-insurance"  label="Insurance Policies"  icon={IconShieldCheck}   color="laranja"   count={accountInsurance.data?.total} />
          <PendingChangeRequestsCard />
          <MyTasksCard />
          <Card component={Link} to="/legal-templates" withBorder shadow="sm"
            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">Blank legal templates</Text>
                <Title order={2} c="petroleo">Templates</Title>
                <Text size="xs" c="dimmed">Unfilled IAA &amp; annexes · PT/EN</Text>
              </Stack>
              <IconFileText size={28} />
            </Group>
          </Card>
          <Card component={Link} to="/library" withBorder shadow="sm"
            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">Presentation Library</Text>
                <Title order={2} c="petroleo">Library</Title>
                <Text size="xs" c="dimmed">Approved sales &amp; marketing files</Text>
              </Stack>
              <IconPresentation size={28} />
            </Group>
          </Card>
          <Card component={Link} to="/saa" withBorder shadow="sm"
            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">SAA</Text>
                <Title order={2} c="petroleo">SAA</Title>
                <Text size="xs" c="dimmed">Strategic allocation return analysis</Text>
              </Stack>
              <IconChartLine size={28} />
            </Group>
          </Card>
          <AdminPageReadTiles />
          <PortfoliosReadTile />
          <ModelsReadTile />
        </SimpleGrid>

        {/* P11 (Pedro 2026-10-10): due dates + birthdays in the next 30 days, every user. */}
        <NextThirtyDaysCard />

        <Text size="xs" c="dimmed" mt="md">
          Charter §10: every state transition above is captured in the audit log with actor and source.
        </Text>
      </Stack>
    </Container>
  );
}
